import React from 'react';
import { useIsCurrentPlanSelected } from '../FinancialPlanOPL/Goals/hooks/useIsCurrentPlanSelected';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { useActionStepsQuery } from './api/useActionStepsQuery';
import { PDFTitle } from '../PDFRenderer/components/PDFTitle/PDFTitle';
import { ActionStepsReportLoading } from './ActionStepsReport.loading';
import { ErrorState } from '../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { GoalSection } from './components/GoalSection/GoalSection';
import { GoalTypeDTO } from '../FinancialPlanOPL/types/goalsDTO';

interface Props {
  clientId: string;
  fpId: string;
  isPrint?: boolean;
  title?: string;
}

export const ActionStepsReport = (props: Props) => {
  const { isPrint, title } = props;

  const { isCurrentPlanSelected } = useIsCurrentPlanSelected();

  const Wrapper = isPrint ? Box : React.Fragment;

  if (isPrint && isCurrentPlanSelected) {
    return null;
  }

  return (
    <Wrapper>
      {!isPrint && (
        <Text as="h1" fontSize="h3" fontWeight={600} mb="16">
          Action Steps
        </Text>
      )}

      {isPrint && title && <PDFTitle title={title} />}

      <Box boxShadow={isPrint ? undefined : '400'}>
        {isCurrentPlanSelected ? (
          <Text px="16" py={20}>
            There are no action steps for the current plan.
          </Text>
        ) : (
          <ActionStepsReportContent {...props} />
        )}
      </Box>
    </Wrapper>
  );
};

const ActionStepsReportContent = ({ isPrint }: Props) => {
  const { isLoading, isError, refetch, data } = useActionStepsQuery();

  if (isLoading) {
    return <ActionStepsReportLoading />;
  }

  if (isError) {
    return (
      <Box p="48" mx="auto">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const {
    Goals,
    Tasks,
    OtherGoals,
    Options: { DebtTypes },
    PlanActionStepsAdvisersAssignees,
    ActionStepAssetBucketOptions: { AllowAssetBucketingCustomization, AvailableAssetBuckets },
  } = data;

  const debtManagementSubGoals = DebtTypes.filter(({ value }) =>
    Tasks.some(({ DebtType }) => DebtType === value),
  );

  const debtManagementGoalId = Goals.find(({ Name }) => Name === 'Debt Management')?.Id;

  return (
    <>
      {Goals.map(({ Id, Name, Type }) => (
        <GoalSection
          key={Id}
          goalId={Id}
          goalName={Name}
          goalType={Type}
          actionSteps={Tasks.filter(
            ({ GoalId, DebtType }) => GoalId === Id && typeof DebtType !== 'number',
          )}
          isPrint={isPrint}
          linkToGoal
          advisorAssignees={PlanActionStepsAdvisersAssignees}
          showAssetBucketing={AllowAssetBucketingCustomization}
          availableAssetBuckets={AvailableAssetBuckets}
        />
      ))}

      {debtManagementSubGoals.map(({ value, label }) => (
        <GoalSection
          key={value}
          goalId={debtManagementGoalId}
          goalName={label}
          goalType={GoalTypeDTO.DebtManagement}
          actionSteps={Tasks.filter(({ DebtType }) => DebtType === value)}
          debtType={value}
          isPrint={isPrint}
          linkToGoal
          advisorAssignees={PlanActionStepsAdvisersAssignees}
          showAssetBucketing={AllowAssetBucketingCustomization}
          availableAssetBuckets={AvailableAssetBuckets}
        />
      ))}

      {OtherGoals.map(({ Category, Name }) => (
        <GoalSection
          key={Category}
          goalName={Name}
          actionSteps={Tasks.filter(({ PulseCheckCategory }) => PulseCheckCategory === Category)}
          pulseCheckCategory={Category}
          isPrint={isPrint}
          advisorAssignees={PlanActionStepsAdvisersAssignees}
          showAssetBucketing={AllowAssetBucketingCustomization}
          availableAssetBuckets={AvailableAssetBuckets}
        />
      ))}

      <GoalSection
        isOther
        goalName="Other Items"
        actionSteps={Tasks.filter(
          ({ GoalId, PulseCheckCategory }) => !GoalId && typeof PulseCheckCategory !== 'number',
        )}
        isPrint={isPrint}
        advisorAssignees={PlanActionStepsAdvisersAssignees}
        showAssetBucketing={AllowAssetBucketingCustomization}
        availableAssetBuckets={AvailableAssetBuckets}
      />
    </>
  );
};
